import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Cog, Wallet, Package, Handshake, AlertTriangle, Users } from 'lucide-react';
import { getVisitorId, getSavedUtmParams } from '../hooks/useVisitorTracking';
import vadimPhoto from '../assets/vadim-photo.png';
import vadimPortrait from '../assets/vadim-portrait.png';
import salesSnapshot from '../assets/vadim-sales-snapshot.webp';
import denisResult from '../assets/denis-result.png';
import nikitaResult from '../assets/nikita-result.png';
import dariaResult from '../assets/daria-result.png';
import marinaResult from '../assets/marina-result.png';
import andreyResult from '../assets/andrey-result.png';
import valeriaResult from '../assets/valeria-result.png';
import planCover from '../assets/plan-cover.jpg';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ CONFIG — заменить, когда будет финальный бот ============
const BOT_USERNAME = 'vadjjik1_bot';
const START_TAG = 'lp1';

const buildBotLink = () => {
  const utm = getSavedUtmParams();
  let payload = START_TAG;
  if (utm.utm_source) {
    payload += '-' + String(utm.utm_source).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 40);
  }
  return `https://t.me/${BOT_USERNAME}?start=${payload.slice(0, 64)}`;
};

// ============ Аналитика ============
const useTracker = () => {
  const utm = useMemo(() => getSavedUtmParams(), []);
  const visitorId = useMemo(() => getVisitorId(), []);
  return useCallback((event, meta = {}) => {
    try {
      fetch(`${API_URL}/api/checker/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_id: visitorId,
          event,
          utm_source: utm.utm_source || null,
          utm_medium: utm.utm_medium || null,
          utm_campaign: utm.utm_campaign || null,
          referrer: document.referrer || null,
          meta,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) { /* ignore */ }
  }, [visitorId, utm]);
};

// ============ Калькулятор — формулы из ТЗ ============
const calcIncome = (b) => {
  let m;
  if (b <= 1000) m = 1.3;
  else if (b >= 2000) m = 1.0;
  else m = 1.3 - 0.3 * (b - 1000) / 1000;
  return {
    m3: Math.round(b * 0.475 * m),
    m6: Math.round(b * 0.875 * m),
  };
};
const usd = (n) => '$' + n.toLocaleString('en-US');

// ============ CTA-кнопка ============
const CtaButton = ({ label, event, tracker, testId, className = '' }) => (
  <a
    href={buildBotLink()}
    onClick={() => tracker(event)}
    data-testid={testId}
    className={`relative inline-flex min-h-[56px] w-full items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 bg-[length:200%_auto] animate-gradient px-10 text-lg font-black uppercase tracking-wide text-black shadow-2xl shadow-amber-500/40 transition-all duration-300 hover:shadow-amber-500/60 active:scale-[0.98] sm:w-auto ${className}`}
  >
    {label}
  </a>
);

// ============ Экран 1 — первый экран ============
const HeroScreen = ({ tracker }) => (
  <section data-testid="lp-hero" className="relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 left-0 h-[500px] w-[500px] rounded-full bg-amber-500/[0.07] blur-[130px]" />
      <div className="absolute right-0 top-40 h-[400px] w-[400px] rounded-full bg-orange-500/[0.05] blur-[120px]" />
    </div>
    <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-6 px-5 pt-7 pb-10 sm:pt-14 lg:flex-row lg:items-center lg:gap-10 lg:pb-20">
      <div className="w-full lg:w-3/5">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1.5 text-[12px] font-semibold uppercase tracking-widest text-amber-300 sm:mb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
          Бесплатный разбор · 40 минут
        </div>
        <h1 className="text-[28px] leading-[1.15] font-black text-white sm:text-[36px] lg:text-[48px] lg:leading-[1.1]">
          Как заработать первые{' '}
          <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300 bg-clip-text text-transparent">
            {'\u0024\u0032\u00A0\u0030\u0030\u0030'} на Amazon
          </span>{' '}
          за 3 месяца
        </h1>
        <p className="mt-3 text-[17px] font-semibold text-slate-200 sm:mt-4 sm:text-xl">
          Без своего бренда и придумывания товара. Берёте то, что уже продаётся.
        </p>
        <div className="mt-4 sm:mt-6">
          <p className="text-[15px] font-bold text-white sm:text-[17px]">За 40 минут вы узнаете:</p>
          <ul className="mt-2 space-y-1.5 text-[15px] text-slate-300 sm:text-[17px]" data-testid="hero-benefits-list">
            <li className="flex gap-2"><span className="font-bold text-amber-400">—</span> какой товар брать</li>
            <li className="flex gap-2"><span className="font-bold text-amber-400">—</span> сколько нужно денег на старт</li>
            <li className="flex gap-2"><span className="font-bold text-amber-400">—</span> сколько вы сможете заработать</li>
            <li className="flex gap-2"><span className="font-bold text-amber-400">—</span> реальные цифры людей, которые уже торгуют на Amazon</li>
          </ul>
          <p className="mt-3 text-[15px] text-slate-400 sm:text-[17px]">
            Плюс пошаговый план запуска под вашу страну и бюджет.
          </p>
        </div>
        <div className="mt-5 sm:mt-7">
          <CtaButton label="Забрать разбор" event="cta_hero" tracker={tracker} testId="cta-hero" />
          <p className="mt-2.5 text-[13px] leading-relaxed text-slate-400">
            Сразу после перехода в Telegram вы получите бесплатный{' '}
            <span className="text-slate-200 font-semibold">40-минутный видеоразбор</span> и{' '}
            <span className="text-slate-200 font-semibold">PDF-план запуска</span>.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-500 sm:mt-8">
          <span><strong className="text-slate-300">5 лет</strong> на Amazon</span>
          <span className="h-3 w-px bg-white/15" />
          <span><strong className="text-slate-300">$900K+</strong> оборот в год</span>
          <span className="h-3 w-px bg-white/15" />
          <span><strong className="text-slate-300">100+</strong> учеников</span>
        </div>
      </div>
      <div className="hidden w-2/5 justify-center lg:flex">
        <div className="relative">
          <div className="absolute -inset-8 rounded-full bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent blur-3xl" />
          <img
            src={vadimPhoto}
            alt="Вадим Малюк — продавец Amazon"
            className="relative max-h-[520px] w-auto object-contain"
            width="400"
            height="520"
          />
        </div>
      </div>
    </div>
  </section>
);

// ============ Экран 2 — узнавание ============
const RecognitionScreen = () => (
  <section data-testid="lp-recognition" className="bg-white/[0.02] py-12 lg:py-20">
    <div className="mx-auto max-w-[680px] px-5">
      <h2 className="text-[22px] font-bold leading-snug text-white sm:text-[30px]">
        Вы уже давно думаете про Amazon. И до сих пор не начали.
      </h2>
      <div className="mt-5 space-y-4 text-[17px] leading-[1.65] text-slate-300 sm:text-lg">
        <p>
          Посмотрели десяток видео. Вроде понятно, что люди зарабатывают. Но каждый раз всё
          упирается в одно: <strong className="text-white">непонятно, с чего конкретно начать вам</strong>.
        </p>
        <p>Сколько нужно денег? Какой товар взять, чтобы он продался? Где его брать?</p>
        <p>Ролики на это не отвечают. Они заканчиваются там, где начинается настоящая работа.</p>
        <p className="font-semibold text-amber-300">Разбор начинается с этого места.</p>
      </div>
    </div>
  </section>
);

// ============ Экран 3 — калькулятор ============
const CalculatorScreen = ({ tracker }) => {
  const [budget, setBudget] = useState(2000);
  const usedRef = useRef(false);
  const lastSentRef = useRef(null);
  const sectionRef = useRef(null);
  const budgetRef = useRef(budget);
  budgetRef.current = budget;

  const { m3, m6 } = calcIncome(budget);

  const onInput = (e) => {
    const v = Number(e.target.value);
    setBudget(v);
    if (!usedRef.current) {
      usedRef.current = true;
      tracker('calc_used');
    }
  };

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let wasVisible = false;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        wasVisible = true;
      } else if (wasVisible && usedRef.current && lastSentRef.current !== budgetRef.current) {
        lastSentRef.current = budgetRef.current;
        tracker('calc_value', { budget: budgetRef.current });
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [tracker]);

  return (
    <section ref={sectionRef} data-testid="lp-calculator" className="py-12 lg:py-20">
      <div className="mx-auto max-w-3xl px-5">
        <div className="text-[12px] font-semibold uppercase tracking-widest text-amber-400">Калькулятор</div>
        <h2 className="mt-2 text-[22px] font-bold text-white sm:text-[30px]">Сколько вы сможете зарабатывать</h2>
        <p className="mt-2 text-[17px] text-slate-400">Подвиньте ползунок — увидите цифры под ваш бюджет</p>

        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm sm:p-8">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-semibold text-slate-200 sm:text-[17px]">Ваш стартовый бюджет</span>
            <span data-testid="calc-budget-value" className="text-[22px] font-medium text-amber-400">{usd(budget)}</span>
          </div>
          <input
            type="range"
            min="500"
            max="10000"
            step="100"
            value={budget}
            onInput={onInput}
            onChange={onInput}
            data-testid="calc-slider"
            className="lp-slider mt-4 w-full"
            aria-label="Ваш стартовый бюджет"
          />
          <div className="mt-1 flex justify-between text-[13px] text-slate-500">
            <span>$500</span>
            <span>$10,000</span>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4 text-center">
              <div className="text-[13px] text-slate-400">Чистыми через 3 месяца</div>
              <div data-testid="calc-result-3m" className="mt-1 text-[28px] font-medium text-white">{usd(m3)}</div>
              <div className="text-[13px] text-slate-400">в месяц</div>
            </div>
            <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4 text-center">
              <div className="text-[13px] text-slate-400">Чистыми через 6 месяцев</div>
              <div data-testid="calc-result-6m" className="mt-1 text-[28px] font-medium text-white">{usd(m6)}</div>
              <div className="text-[13px] text-slate-400">в месяц</div>
            </div>
            <div className="select-none rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-center" aria-disabled="true">
              <div className="text-[13px] text-slate-500">Какой товар брать</div>
              <div className="mt-1 text-[22px] text-slate-500">🔒</div>
              <div className="text-[13px] text-slate-500">в разборе</div>
            </div>
            <div className="select-none rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-center" aria-disabled="true">
              <div className="text-[13px] text-slate-500">Сколько времени уходит</div>
              <div className="mt-1 text-[22px] text-slate-500">🔒</div>
              <div className="text-[13px] text-slate-500">в разборе</div>
            </div>
          </div>

          <p className="mt-4 text-[13px] text-slate-500">Расчёт по средним показателям учеников. Не гарантия результата.</p>
        </div>

        <div className="mt-6 text-center sm:text-left">
          <CtaButton label="Узнать, как это сделать" event="cta_calc" tracker={tracker} testId="cta-calc" />
        </div>
      </div>
    </section>
  );
};

// ============ Экран 4 — что в разборе ============
const CONTENT_ITEMS = [
  { icon: Cog, t: 'Как это вообще работает', d: 'Находите товар, который уже покупают. Берёте у поставщика дешевле, продаёте на Amazon дороже.' },
  { icon: Wallet, t: 'Сколько денег нужно на старте', d: 'Спойлер: меньше, чем вам говорили.' },
  { icon: Package, t: 'Какой товар брать новичку', d: 'И какой не брать никогда, даже если кажется, что он классный.' },
  { icon: Handshake, t: 'Где найти поставщика', d: 'И как с ним разговаривать, чтобы дал нормальную цену.' },
  { icon: AlertTriangle, t: '3 ошибки, из-за которых новички теряют деньги', d: 'Про них не пишут в статьях.' },
  { icon: Users, t: 'Реальные истории людей', d: 'С чего начинали, сколько вложили, что имеют сейчас.' },
];

const ContentScreen = () => (
  <section data-testid="lp-content" className="relative overflow-hidden bg-white/[0.02] py-12 lg:py-20">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-20 right-0 h-[350px] w-[350px] rounded-full bg-amber-500/[0.06] blur-[110px]" />
      <div className="absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-orange-500/[0.05] blur-[110px]" />
    </div>
    <div className="relative mx-auto max-w-3xl px-5">
      <div className="text-[12px] font-semibold uppercase tracking-widest text-amber-400">Содержание</div>
      <h2 className="mt-2 text-[22px] font-bold text-white sm:text-[30px]">Что будет в разборе</h2>
      <p className="mt-2 text-[15px] text-slate-400 sm:text-[17px]">6 блоков — от нуля до готового плана. Смотрится за один вечер.</p>

      <div className="relative mt-9">
        <div className="absolute left-[23px] top-3 bottom-3 w-px bg-gradient-to-b from-amber-500/70 via-amber-500/25 to-amber-500/70 sm:left-[27px]" aria-hidden="true" />
        <ol className="space-y-3 sm:space-y-4">
          {CONTENT_ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <li key={i} data-testid={`content-item-${i}`} className="group relative flex gap-4 sm:gap-5">
                <div className="relative z-10 flex-none pt-1">
                  <div className="flex h-[46px] w-[46px] items-center justify-center rounded-2xl border border-amber-500/40 bg-[#0c0c0e] shadow-lg shadow-amber-500/10 transition-all duration-300 group-hover:border-amber-400/70 group-hover:shadow-amber-500/30 sm:h-[54px] sm:w-[54px]">
                    <Icon className="h-5 w-5 text-amber-400 sm:h-6 sm:w-6" />
                  </div>
                </div>
                <div className="min-w-0 flex-1 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm transition-all duration-300 group-hover:border-amber-400/30 group-hover:bg-white/[0.05] sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-[16px] font-bold leading-snug text-white sm:text-[18px]">{item.t}</div>
                    <span className="flex-none text-[13px] font-black tabular-nums text-amber-500/40">0{i + 1}</span>
                  </div>
                  <div className="mt-1.5 text-[14px] leading-relaxed text-slate-400 sm:text-[15px]">{item.d}</div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div data-testid="content-bonus" className="relative mt-8 overflow-hidden rounded-2xl border border-amber-500/50 p-[1px]">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/25 via-orange-500/10 to-amber-500/25" />
        <div className="relative rounded-2xl bg-[#0b0b0d]/90 p-5 sm:p-7">
          <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-amber-500/25 blur-3xl" />
          <div className="relative flex items-center gap-5 sm:gap-7">
            <div className="relative flex-none">
              <div className="absolute -inset-2.5 rounded-xl bg-amber-500/35 blur-xl" />
              <img
                src={planCover}
                alt="PDF-план запуска"
                loading="lazy"
                width="92"
                height="130"
                className="relative h-[130px] w-[92px] rotate-[-3deg] rounded-lg border border-amber-300/40 object-cover shadow-2xl transition-transform duration-300 hover:rotate-0"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-black">
                🎁 Бонус в конце
              </div>
              <div className="mt-2 text-[18px] font-bold text-white sm:text-[21px]">PDF-план запуска</div>
              <div className="mt-1 text-[14px] leading-relaxed text-slate-300 sm:text-[16px]">
                Что делать в первую, вторую и третью неделю. Под ваш бюджет.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============ Экран 5 — автор ============
const AuthorScreen = () => (
  <section data-testid="lp-author" className="py-12 lg:py-20">
    <div className="mx-auto max-w-5xl px-5">
      <div className="text-[12px] font-semibold uppercase tracking-widest text-amber-400">Автор разбора</div>
      <h2 className="mt-2 text-[22px] font-bold text-white sm:text-[30px]">Кто я такой</h2>
      <div className="mt-7 flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex flex-none justify-center lg:w-[300px]">
          <div className="relative">
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-amber-500/25 to-orange-500/10 blur-2xl" />
            <img
              src={vadimPortrait}
              alt="Вадим Малюк"
              loading="lazy"
              width="280"
              height="371"
              className="relative w-[240px] rounded-2xl border border-white/10 object-cover sm:w-[280px]"
            />
          </div>
        </div>
        <div className="max-w-[680px]">
          <div className="space-y-4 text-[17px] leading-[1.65] text-slate-300 sm:text-lg">
            <p>Меня зовут Вадим Малюк. Продаю на Amazon с 2020 года — уже 5 лет.</p>
            <p>
              Начинал не с капитала, а с работы ресёрчером: анализировал товары для чужих
              магазинов. Разобрался, как это устроено изнутри, и запустил своё.
            </p>
            <p>
              Сегодня <strong className="text-white">оборот</strong> моих магазинов — больше{' '}
              <strong className="text-amber-300">$900 000 в год</strong>, в команде 7 человек, продажи
              идут по европейским площадкам.
            </p>
            <p>
              За это время через меня прошло больше 100 человек. Они стартовали из Польши, Италии,
              Германии, Испании, Ирландии — с разными бюджетами и без опыта.
            </p>
            <p>
              В разборе показываю не теорию, а то, как это работает у меня и у них: реальные товары,
              реальные поставщики, реальные ошибки.
            </p>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              ['5 лет', 'на Amazon'],
              ['$900K+', 'оборот в год'],
              ['100+', 'учеников'],
            ].map(([big, small], i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-center">
                <div className="text-lg font-bold text-amber-400 sm:text-xl">{big}</div>
                <div className="text-[13px] text-slate-400">{small}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white p-1.5 sm:p-2">
        <img
          src={salesSnapshot}
          alt="Кабинет продавца Amazon — оборот магазина Вадима"
          loading="lazy"
          style={{ aspectRatio: '1188 / 485' }}
          className="w-full rounded-xl object-contain"
        />
      </div>
      <p className="mt-2 text-center text-[13px] text-slate-500">Скриншот из моего кабинета продавца Amazon</p>
    </div>
  </section>
);

// ============ Экран 6 — результаты ============
const CASES = [
  { name: 'Денис', flag: '🇵🇱', country: 'Польша', start: '$800', days: 7, note: '$236 оборот за первую неделю', img: denisResult },
  { name: 'Никита', flag: '🇩🇪', country: 'Германия', start: '$500', days: 10, note: '€114 оборот за неделю', img: nikitaResult },
  { name: 'Дария', flag: '🇮🇹', country: 'Италия', start: '$1,500', days: 14, note: '€485 оборот за день', img: dariaResult },
  { name: 'Марина', flag: '🇨🇦', country: 'Канада', start: '$2,000', days: 30, note: 'CA$1,870 оборот за месяц', img: marinaResult },
  { name: 'Андрей', flag: '🇵🇹', country: 'Португалия', start: '$2,500', days: 26, note: '$2,170 оборот', img: andreyResult },
  { name: 'Валерия', flag: '🇵🇱', country: 'Польша', start: '$5,000', days: 24, note: '€32,348 оборот за месяц', img: valeriaResult },
];

const ResultsScreen = () => {
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === 'Escape' && setZoom(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  return (
    <section data-testid="lp-results" className="bg-white/[0.02] py-12 lg:py-20">
      <div className="mx-auto max-w-6xl px-5">
        <div className="text-[12px] font-semibold uppercase tracking-widest text-amber-400">Результаты учеников</div>
        <h2 className="mt-2 text-[22px] font-bold text-white sm:text-[30px]">Что получается у обычных людей</h2>
        <p className="mt-2 text-[17px] text-slate-400">Скриншоты из кабинетов продавцов</p>
        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CASES.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setZoom(c)}
              data-testid={`case-card-${i}`}
              className="group rounded-2xl border border-white/10 bg-white/[0.03] text-left transition-all duration-300 hover:border-amber-400/40"
            >
              <div className="flex h-[180px] items-center justify-center overflow-hidden rounded-t-2xl bg-white/[0.04] p-2">
                <img src={c.img} alt={`Кабинет продавца — ${c.name}`} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
              </div>
              <div className="p-4">
                <div className="text-[15px] font-bold text-white sm:text-[17px]">
                  {c.name} {c.flag} {c.country} · старт {c.start}
                </div>
                <div className="mt-0.5 text-[15px] text-amber-300">Первые деньги через {c.days} дней</div>
                <div className="mt-1 text-[13px] text-slate-500">{c.note}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {zoom && (
        <div
          onClick={() => setZoom(null)}
          data-testid="case-lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
        >
          <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-3xl">
            <button
              type="button"
              onClick={() => setZoom(null)}
              data-testid="case-lightbox-close"
              aria-label="Закрыть"
              className="absolute -top-4 -right-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-xl font-bold text-black transition-transform hover:scale-105"
            >
              ×
            </button>
            <img src={zoom.img} alt={zoom.name} className="max-h-[85vh] w-full rounded-2xl bg-[#0b0b0d] object-contain" />
            <div className="mt-2 rounded-xl bg-black/70 px-4 py-2 text-[15px] text-white backdrop-blur-sm">
              <strong>{zoom.name}</strong> {zoom.flag} {zoom.country} · старт {zoom.start} ·{' '}
              <span className="text-amber-300">первые деньги через {zoom.days} дней</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ============ Экран 7 — возражения ============
const OBJECTIONS = [
  { q: '«…я не знаю английский»', a: 'В эру ИИ этого и не нужно. А свободно говорить не может 99% людей, которые начинали на Amazon, — я в том числе.' },
  { q: '«…у меня мало денег»', a: 'Для старта их много и не нужно. Более того — много денег на старте опасно. На видео расскажу почему.' },
  { q: '«…я живу не в Европе или США»', a: 'Большинство тех, кто торгует, там не живут — это не обязательно.' },
  { q: '«…я ничего в этом не понимаю»', a: 'Так и должно быть. Разбор как раз для тех, кто ни разу не пробовал.' },
  { q: '«…рынок уже забит»', a: 'Рынок полон только там, где есть деньги. Забит верх, а не рынок.' },
  { q: '«…у меня нет времени»', a: 'На старте хватает 2 часов в день.' },
  { q: '«…это точно бесплатно?»', a: 'Да. Никакой оплаты и карт. Только Telegram.' },
];

const ObjectionsScreen = () => (
  <section data-testid="lp-objections" className="py-12 lg:py-20">
    <div className="mx-auto max-w-[680px] px-5">
      <div className="text-[12px] font-semibold uppercase tracking-widest text-amber-400">Частые сомнения</div>
      <h2 className="mt-2 text-[22px] font-bold text-white sm:text-[30px]">«У меня не получится, потому что…»</h2>
      <ul className="mt-7 space-y-5">
        {OBJECTIONS.map((o, i) => (
          <li key={i} data-testid={`objection-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[17px] font-bold text-white">{o.q}</div>
            <div className="mt-0.5 text-[15px] leading-relaxed text-slate-400 sm:text-[17px]">{o.a}</div>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// ============ Экран 8 — финал + подвал ============
const FinalScreen = ({ tracker, finalRef }) => (
  <section ref={finalRef} data-testid="lp-final" className="relative overflow-hidden py-14 lg:py-24">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute bottom-0 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-amber-500/[0.07] blur-[130px]" />
    </div>
    <div className="relative mx-auto max-w-[680px] px-5 text-center">
      <h2 className="text-[22px] font-bold text-white sm:text-[30px]">
        Через 40 минут вы будете точно знать, что делать
      </h2>
      <p className="mt-4 text-[17px] leading-[1.65] text-slate-300 sm:text-lg">
        Или поймёте, что Amazon вам не подходит. Тоже нормальный результат — лучше сейчас, чем после
        закупки товара.
      </p>
      <div className="mt-7">
        <CtaButton label="Смотреть разбор" event="cta_final" tracker={tracker} testId="cta-final" />
      </div>
    </div>
  </section>
);

const LegalFooter = () => {
  const [doc, setDoc] = useState(null);
  return (
    <footer data-testid="lp-footer" className="border-t border-white/10 py-6">
      <div className="mx-auto max-w-6xl px-5 text-center text-[13px] leading-relaxed text-slate-500">
        ФОП Малюк Вадим ·{' '}
        <button type="button" onClick={() => setDoc('privacy')} data-testid="footer-privacy" className="underline transition-colors hover:text-slate-300">
          Политика конфиденциальности
        </button>{' '}
        ·{' '}
        <button type="button" onClick={() => setDoc('offer')} data-testid="footer-offer" className="underline transition-colors hover:text-slate-300">
          Оферта
        </button>{' '}
        · Результаты зависят от рынка, капитала и действий
      </div>
      {doc && (
        <div onClick={() => setDoc(null)} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="legal-modal">
          <div onClick={(e) => e.stopPropagation()} className="max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-white/10 bg-[#111214] p-6 text-left">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-bold text-white">
                {doc === 'privacy' ? 'Политика конфиденциальности' : 'Публичная оферта'}
              </h3>
              <button type="button" onClick={() => setDoc(null)} data-testid="legal-modal-close" aria-label="Закрыть" className="text-xl font-bold text-slate-500 hover:text-white">×</button>
            </div>
            <div className="mt-4 space-y-3 text-[14px] leading-relaxed text-slate-400">
              {doc === 'privacy' ? (
                <>
                  <p>Оператор данных: ФОП Малюк Вадим. Мы обрабатываем только технические данные посещения (IP-адрес, тип устройства, источник перехода) для аналитики работы страницы.</p>
                  <p>Страница не собирает имя, email или телефон. Переход в Telegram регулируется политикой конфиденциальности Telegram.</p>
                  <p>Данные не передаются третьим лицам и хранятся не дольше, чем необходимо для аналитики. По вопросам обработки данных — обратитесь через Telegram-бот.</p>
                </>
              ) : (
                <>
                  <p>Настоящий документ является официальным предложением ФОП Малюк Вадим о предоставлении бесплатного информационного материала (видеоразбор и PDF-план) через Telegram.</p>
                  <p>Материал предоставляется бесплатно, без оплаты и ввода платёжных данных. Материал носит информационный характер и не является финансовой или инвестиционной рекомендацией.</p>
                  <p>Результаты зависят от рынка, капитала и действий получателя и не гарантируются.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

// ============ Липкая кнопка на мобиле ============
const StickyCta = ({ tracker, visible }) => (
  <div
    className="fixed inset-x-0 bottom-0 z-40 p-2 transition-transform duration-300 sm:hidden"
    style={{ transform: visible ? 'translateY(0)' : 'translateY(120%)' }}
    data-testid="sticky-cta-wrap"
  >
    <a
      href={buildBotLink()}
      onClick={() => tracker('cta_sticky')}
      data-testid="cta-sticky"
      className="flex h-[56px] w-full items-center justify-center rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-base font-black uppercase tracking-wide text-black shadow-2xl shadow-amber-500/40"
    >
      Забрать разбор
    </a>
  </div>
);

// ============ Страница ============
export default function CheckerPage() {
  const tracker = useTracker();
  const heroEndRef = useRef(null);
  const finalRef = useRef(null);
  const [pastHero, setPastHero] = useState(false);
  const [finalVisible, setFinalVisible] = useState(false);

  useEffect(() => {
    document.title = 'Как заработать первые $2 000 на Amazon за 3 месяца';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = 'Бесплатный разбор 40 минут + PDF-план запуска под ваш бюджет и страну';

    tracker('page_view');

    let scrolled50 = false;
    const onScroll = () => {
      if (scrolled50) return;
      const h = document.documentElement;
      if (h.scrollTop / (h.scrollHeight - h.clientHeight) >= 0.5) {
        scrolled50 = true;
        tracker('scroll_50');
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [tracker]);

  useEffect(() => {
    const heroObs = new IntersectionObserver(
      ([e]) => setPastHero(!e.isIntersecting && e.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    const finalObs = new IntersectionObserver(([e]) => setFinalVisible(e.isIntersecting), { threshold: 0.15 });
    if (heroEndRef.current) heroObs.observe(heroEndRef.current);
    if (finalRef.current) finalObs.observe(finalRef.current);
    return () => { heroObs.disconnect(); finalObs.disconnect(); };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#050506] text-slate-200 antialiased"
      style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
      data-testid="checker-page"
    >
      <style>{`
        .lp-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 44px;
          background: transparent;
          cursor: pointer;
        }
        .lp-slider:focus { outline: none; }
        .lp-slider::-webkit-slider-runnable-track {
          height: 6px;
          border-radius: 3px;
          background: rgba(255,255,255,0.12);
        }
        .lp-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          margin-top: -11px;
          height: 28px;
          width: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f59e0b, #f97316);
          border: 3px solid #050506;
          box-shadow: 0 0 12px rgba(245,158,11,0.5);
        }
        .lp-slider::-moz-range-track {
          height: 6px;
          border-radius: 3px;
          background: rgba(255,255,255,0.12);
        }
        .lp-slider::-moz-range-thumb {
          height: 28px;
          width: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f59e0b, #f97316);
          border: 3px solid #050506;
          box-shadow: 0 0 12px rgba(245,158,11,0.5);
        }
      `}</style>

      <HeroScreen tracker={tracker} />
      <div ref={heroEndRef} aria-hidden="true" />
      <RecognitionScreen />
      <CalculatorScreen tracker={tracker} />
      <ContentScreen />
      <AuthorScreen />
      <ResultsScreen />
      <ObjectionsScreen />
      <FinalScreen tracker={tracker} finalRef={finalRef} />
      <LegalFooter />

      <StickyCta tracker={tracker} visible={pastHero && !finalVisible} />
    </div>
  );
}
